import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, MoreHorizontal, Shield } from 'lucide-react';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import {
  getActiveNavigationSectionId,
  getSidebarNavigation,
  type NavigationItemConfig,
} from './navigationRegistry';
import { useAuthz } from '../../hooks/useAuthz';

function SidebarTooltip({
  label,
  collapsed,
  children,
}: {
  label: string;
  collapsed: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);

  const handleMouseEnter = () => {
    if (!collapsed) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 12,
      });
    }
    timeoutRef.current = window.setTimeout(() => setOpen(true), 140);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
    setOpen(false);
  };

  useEffect(() => () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
  }, []);

  return (
    <div
      ref={triggerRef}
      className="relative flex justify-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {collapsed && open && position && (
        <div
          className="pointer-events-none fixed z-[120] -translate-y-1/2 whitespace-nowrap rounded-[4px] border border-enterprise-border-strong bg-[#ffffff] px-2 py-1 text-[11px] font-medium text-enterprise-text-primary shadow-[0_2px_8px_rgba(15,23,42,0.08)] dark:bg-slate-900"
          style={{ top: position.top, left: position.left }}
        >
          {label}
        </div>
      )}
    </div>
  );
}

function SectionLabel({
  children,
  collapsed,
}: {
  children: React.ReactNode;
  collapsed: boolean;
}) {
  if (collapsed) {
    return <div className="h-2" aria-hidden="true" />;
  }

  return (
    <p className="px-[10px] pb-2 pt-2 text-[10px] font-semibold tracking-[0.05em] uppercase text-slate-500">
      {children}
    </p>
  );
}

function navLinkClass(isActive: boolean, collapsed: boolean) {
  return [
    'group relative flex w-full items-center rounded-none text-[12px] transition-colors duration-150 ease-in-out overflow-hidden',
    collapsed
      ? 'flex-col justify-center items-center w-10 h-10 mx-auto p-0'
      : 'gap-1.5 pl-[10px] pr-[6px] py-[6px]',
    isActive
      ? 'font-semibold'
      : '',
  ].join(' ');
}

function SidebarNavItem({
  item,
  collapsed,
}: {
  item: NavigationItemConfig;
  collapsed: boolean;
}) {
  const iconNode = React.isValidElement(item.icon)
    ? React.cloneElement(item.icon as React.ReactElement<any>, { size: collapsed ? 22 : 18, strokeWidth: 1.75 })
    : item.icon;

  return (
    <SidebarTooltip label={item.label} collapsed={collapsed}>
      <NavLink key={item.id} to={item.path} end={item.end} className={({ isActive }) => navLinkClass(isActive, collapsed)}>
        {({ isActive }) => (
          <>
            {/* Active left-border indicator */}
            <span
              className="absolute left-0 top-0 h-full"
              style={{
                width: 3,
                borderRadius: 0,
                backgroundColor: isActive ? 'var(--nav-active-indicator)' : 'transparent',
              }}
              aria-hidden="true"
            />
            <span
              className={`flex items-center justify-center shrink-0 ${collapsed ? 'h-6 w-6' : 'h-[18px] w-[18px]'}`}
              style={{ color: isActive ? 'var(--nav-item-text-active)' : 'var(--nav-item-text)' }}
            >
              {iconNode}
            </span>
            {!collapsed && (
              <span
                className="truncate"
                style={{ color: isActive ? 'var(--nav-item-text-active)' : 'var(--nav-item-text)' }}
              >
                {item.label}
              </span>
            )}
          </>
        )}
      </NavLink>
    </SidebarTooltip>
  );
}

export interface EnterpriseSidebarProps {
  immersive?: boolean;
  immersiveOpen?: boolean;
  focusMode?: boolean;
}

export const EnterpriseSidebar: React.FC<EnterpriseSidebarProps> = ({
  immersive = false,
  immersiveOpen = true,
  focusMode = false,
}) => {
  const location = useLocation();
  const { workspace } = useWorkspace();
  const { can } = useAuthz();
  const sidebarNavigation = useMemo(
    () => getSidebarNavigation(workspace?.userRole ? { role: workspace.userRole } : null),
    [workspace?.userRole],
  );
  const mainNavigation = useMemo(
    () => sidebarNavigation.filter((group) => group.section.id !== 'institucional'),
    [sidebarNavigation],
  );

  // CONFIGURAÇÃO section — only shown when user can view administration.
  // "Conta" (account) is split out as a standalone footer item always shown.
  const rawFooter = useMemo(
    () => sidebarNavigation.find((group) => group.section.id === 'institucional') ?? null,
    [sidebarNavigation],
  );
  const canViewAdmin = can('view', 'administration');
  const configNavigation = useMemo(
    () =>
      canViewAdmin && rawFooter
        ? { ...rawFooter, items: rawFooter.items.filter((i) => i.id !== 'account') }
        : null,
    [canViewAdmin, rawFooter],
  );
  const accountItem = useMemo(
    () => rawFooter?.items.find((i) => i.id === 'account') ?? null,
    [rawFooter],
  );
  const activeSectionId = getActiveNavigationSectionId(location.pathname);
  const [collapsed, setCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => ({
    ...(activeSectionId ? { [activeSectionId]: true } : {}),
  }));

  const isCollapsedView = focusMode || (collapsed && !immersive);

  const toggleSection = useCallback((id: string) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  useEffect(() => {
    if (activeSectionId) {
      setExpandedSections((prev) => (prev[activeSectionId] ? prev : { ...prev, [activeSectionId]: true }));
    }
  }, [activeSectionId]);

  // Sync current sidebar width as a CSS var so fixed overlays can react
  useEffect(() => {
    const w = focusMode
      ? '56px'
      : (collapsed && !immersive)
        ? 'var(--sidebar-collapsed-width, 48px)'
        : 'var(--sidebar-width, 156px)';
    document.documentElement.style.setProperty('--sidebar-effective-width', w);
  }, [collapsed, immersive, focusMode]);

  const sidebarHeight = 'calc(100vh - var(--shell-statusbar-height, 24px))';

  return (
    <aside
      className={`enterprise-sidebar-surface enterprise-font relative z-20 flex flex-col border-r border-enterprise-border-strong dark:border-white/10 transition-all duration-200 ease-in-out ${
        immersive
          ? `fixed left-0 top-0 z-[70] w-[var(--sidebar-width)] enterprise-surface-2 ${
              immersiveOpen ? 'translate-x-0' : '-translate-x-full'
            }`
          : `shrink-0 ${focusMode ? 'w-[56px]' : collapsed ? 'w-[var(--sidebar-collapsed-width)]' : 'w-[var(--sidebar-width)]'}`
      }`}
      style={{ height: sidebarHeight }}
    >
      <div className="h-[var(--sidebar-header-height)] border-b border-enterprise-border-strong">
        <SidebarTooltip
          label={isCollapsedView ? 'Expandir sidebar' : 'Recolher sidebar'}
          collapsed={isCollapsedView}
        >
          <button
            type="button"
            onClick={() => {
              if (!immersive && !focusMode) {
                setCollapsed((prev) => !prev);
              }
            }}
            className={`flex w-full min-w-0 items-center rounded-sm text-left transition-colors ${
              isCollapsedView
                ? 'h-[var(--sidebar-header-height)] justify-center px-0 hover:bg-white dark:hover:bg-white/10'
                : 'h-[var(--sidebar-header-height)] gap-1.5 pl-[10px] pr-2 hover:bg-white dark:hover:bg-white/10'
            } ${immersive || focusMode ? 'cursor-default hover:bg-transparent' : ''}`}
            aria-label={isCollapsedView ? 'Expandir sidebar' : 'Recolher sidebar'}
          >
            <img src="./assets/logo/logo-oficial.png" alt="Logo" className="h-5 w-auto object-contain opacity-90 dark:opacity-80" />
            {(!isCollapsedView || immersive) && (
              <span className="min-w-0 whitespace-nowrap text-[10px] font-semibold tracking-[0.06em] leading-none text-slate-300">
                WORKFLOW MANAGER
              </span>
            )}
          </button>
        </SidebarTooltip>
      </div>

      <nav className={`flex-1 overflow-y-auto ${isCollapsedView ? 'space-y-2 px-1 py-1.5' : 'space-y-2 px-1.25 py-1.5'}`}>
        {mainNavigation.map(({ section, items }) => {
          if (section.collapsible) {
            const isExpanded = !!expandedSections[section.id];
            const sectionIcon = section.id === 'administracao'
              ? <Shield size={16} />
              : <MoreHorizontal size={16} />;
            return (
              <div
                key={section.id}
                className={`overflow-hidden rounded-sm ${isCollapsedView ? 'px-0 py-0.5' : ''}`}
              >
                <SidebarTooltip label={section.label} collapsed={collapsed && !immersive}>
                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    className={`flex w-full items-center text-left px-3 pt-2 pb-2 text-[10px] uppercase font-semibold tracking-[0.05em] text-slate-500 transition-all duration-150 ease-in-out hover:opacity-70 ${
                      isCollapsedView ? 'justify-center px-0 py-[5px]' : 'gap-1.5 px-1.5 py-[5px]'
                    }`}
                  >
                    {sectionIcon}
                    {(!isCollapsedView || immersive) && (
                      <>
                        <span className="flex-1">{section.label}</span>
                        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </>
                    )}
                  </button>
                </SidebarTooltip>
                {isExpanded && (
                  <div className={`space-y-0.5 ${(collapsed && !immersive) ? 'p-0.5' : 'px-[5px] pb-[5px]'}`}>
                    {items.map((item) => (
                      <SidebarNavItem
                        key={item.id}
                        item={item}
                        collapsed={isCollapsedView}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
              <div key={section.id} className="space-y-1">
              <SectionLabel collapsed={isCollapsedView}>{section.label}</SectionLabel>
              <div className="space-y-1.5">
                {items.map((item) => (
                  <SidebarNavItem key={item.id} item={item} collapsed={isCollapsedView} />
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      {/* CONFIGURAÇÃO block — only for users with administration.view */}
      {configNavigation && configNavigation.items.length > 0 && (
        <div
          className={`shrink-0 border-t border-enterprise-border-strong ${
            isCollapsedView ? 'space-y-2 px-1 py-1.5' : 'space-y-2 px-1.25 py-1.5'
          }`}
        >
          <SectionLabel collapsed={isCollapsedView}>{configNavigation.section.label}</SectionLabel>
          <div className="space-y-1.5">
            {configNavigation.items.map((item) => (
              <SidebarNavItem key={item.id} item={item} collapsed={isCollapsedView} />
            ))}
          </div>
        </div>
      )}

      {/* Conta — always shown, standalone at the very bottom */}
      {accountItem && (
        <div
          className={`shrink-0 border-t border-enterprise-border-strong ${
            isCollapsedView ? 'px-1 py-1.5' : 'px-1.25 py-1.5'
          }`}
        >
          <SidebarNavItem item={accountItem} collapsed={isCollapsedView} />
        </div>
      )}
    </aside>
  );
};

export default EnterpriseSidebar;
